/**
 * FormLabel - Label con tooltip opcional para formularios de contenedores
 *
 * @version 1.0
 * @updated 2026-02-22
 * @module core/modules/waste/components/panels
 * @note Tooltips use React Portal to escape sidebar overflow
 * @description Sigue exactamente el patrón de core/map-tools/TreeDetailsPanel/FormLabel.tsx
 */

import { HelpCircle } from 'lucide-react';
import { useRef, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

export interface FormLabelProps {
  htmlFor?: string;
  required?: boolean;
  tooltip?: string;
  children: React.ReactNode;
}

export function FormLabel({ htmlFor, required, tooltip, children }: FormLabelProps): JSX.Element {
  const iconRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (!iconRef.current || !isHovered) return;

    const updatePosition = () => {
      if (!iconRef.current) return;
      const iconRect = iconRef.current.getBoundingClientRect();

      // Usar un ancho estimado del tooltip (280px max-width)
      const tooltipWidth = 280;
      const padding = 16; // Padding desde los bordes de la ventana

      // Calcular posición centrada respecto al ícono
      let left = iconRect.left + (iconRect.width / 2) - (tooltipWidth / 2);

      // Ajustar si se sale por la izquierda
      if (left < padding) {
        left = padding;
      }

      // Ajustar si se sale por la derecha
      if (left + tooltipWidth > window.innerWidth - padding) {
        left = window.innerWidth - tooltipWidth - padding;
      }

      setPosition({
        top: iconRect.top - 8,
        left: left
      });
    };

    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isHovered]);

  return (
    <label className={`container-form-label ${required ? 'required' : ''}`} htmlFor={htmlFor}>
      <span>{children}</span>
      {tooltip && (
        <>
          <span
            className="container-form-tooltip"
            ref={iconRef}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <HelpCircle size={14} className="container-form-tooltip-icon" />
          </span>
          {isHovered && createPortal(
            <div
              ref={tooltipRef}
              className="container-form-tooltip-portal"
              style={{
                position: 'fixed',
                top: `${position.top}px`,
                left: `${position.left}px`,
                transform: 'translateY(-100%)',
                width: 'max-content',
                maxWidth: '280px',
                padding: '10px 14px',
                background: 'var(--bg-primary)',
                color: 'var(--text-primary)',
                fontSize: '12px',
                fontWeight: '400',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--accent-primary)',
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.4)',
                zIndex: 99999,
                pointerEvents: 'none',
                whiteSpace: 'normal',
                lineHeight: '1.5',
                textAlign: 'left'
              }}
            >
              {tooltip}
            </div>,
            document.body
          )}
        </>
      )}
    </label>
  );
}
